import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Search, X, Calendar } from 'lucide-react';
import toast from 'react-hot-toast';
import ImageUpload from '../components/ImageUpload';
import { getEvents, addEvent, updateEvent, deleteEvent } from '../firebase/firestore';

const EMPTY = {
  title: '', description: '', category: 'music', date: '', time: '',
  venue: '', city: '', country: '', organizer: '',
  minPrice: '', maxPrice: '', totalSeats: '', availableSeats: '',
  active: true, featured: false, trending: false,
  thumbnailEmoji: '🎵', imageUrl: '',
  color1: 0xFF6C3483, color2: 0xFF9B59B6,
};

const CATEGORIES = ['music', 'sports', 'conference', 'art', 'food', 'comedy', 'festival'];
const EMOJIS = { music: '🎵', sports: '🏆', conference: '💻', art: '🎨', food: '🍜', comedy: '😂', festival: '🎉' };

export default function Events() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [imageFile, setImageFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    getEvents().then(d => { setEvents(d); setLoading(false); });
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => { setForm(EMPTY); setImageFile(null); setModal('add'); };
  const openEdit = e => { setForm({ ...e }); setImageFile(null); setModal(e); };
  const close = () => { setModal(null); setImageFile(null); setUploadProgress(0); };

  const f = (key, val) => setForm(p => ({ ...p, [key]: val }));

  const save = async () => {
    if (!form.title || !form.date || !form.venue) { toast.error('Fill required fields'); return; }
    setSaving(true);
    try {
      const data = {
        ...form,
        minPrice: Number(form.minPrice), maxPrice: Number(form.maxPrice),
        totalSeats: Number(form.totalSeats), availableSeats: Number(form.availableSeats),
        thumbnailEmoji: EMOJIS[form.category] || '🎵',
      };
      if (modal === 'add') {
        await addEvent(data, imageFile, setUploadProgress);
        toast.success('Event added');
      } else {
        await updateEvent(modal.id, data, imageFile, setUploadProgress);
        toast.success('Event updated');
      }
      close(); load();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  const remove = async e => {
    if (!window.confirm(`Delete "${e.title}"?`)) return;
    await deleteEvent(e.id, e.imageUrl);
    toast.success('Deleted'); load();
  };

  const filtered = events.filter(e =>
    `${e.title} ${e.city} ${e.category}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-white font-bold text-xl">Events</h2>
          <p className="text-[#546E7A] text-sm">{events.length} events</p>
        </div>
        <button onClick={openAdd}
          className="flex items-center gap-2 px-4 py-2.5 bg-[#9B59B6] hover:bg-[#6C3483]
            text-white rounded-xl text-sm font-semibold transition-colors">
          <Plus size={16} /> Add Event
        </button>
      </div>

      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#546E7A]" />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search events..."
          className="w-full pl-9 pr-4 py-2.5 bg-[#1A2235] border border-[#1E2D45] rounded-xl
            text-white text-sm placeholder-[#546E7A] focus:outline-none focus:border-[#9B59B6]" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-3 text-center py-12 text-[#546E7A]">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="col-span-3 text-center py-12 text-[#546E7A]">No events found</div>
        ) : filtered.map(e => (
          <div key={e.id} className="bg-[#1A2235] border border-[#1E2D45] rounded-2xl overflow-hidden">
            {/* Image or emoji */}
            <div className="h-36 relative overflow-hidden"
              style={{ background: `linear-gradient(135deg, #${(e.color1 || 0xFF6C3483).toString(16).slice(2)}, #${(e.color2 || 0xFF9B59B6).toString(16).slice(2)})` }}>
              {e.imageUrl ? (
                <img src={e.imageUrl} alt={e.title} className="w-full h-full object-cover" />
              ) : (
                <div className="flex items-center justify-center h-full text-5xl">
                  {e.thumbnailEmoji || '🎵'}
                </div>
              )}
              <div className="absolute top-2 left-2 flex gap-1">
                {e.featured && <span className="text-xs px-2 py-0.5 bg-[#D4AF37]/90 text-black rounded-full font-bold">Featured</span>}
                {e.trending && <span className="text-xs px-2 py-0.5 bg-red-500/90 text-white rounded-full font-bold">Trending</span>}
              </div>
              <div className="absolute top-2 right-2">
                <span className={`text-xs px-2 py-0.5 rounded-full font-semibold
                  ${e.active ? 'bg-green-500/80 text-white' : 'bg-gray-500/80 text-white'}`}>
                  {e.active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
            <div className="p-4">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="text-white font-semibold text-sm leading-tight">{e.title}</h3>
                  <p className="text-[#546E7A] text-xs mt-0.5">{e.date} • {e.city}</p>
                </div>
                <span className="text-[#D4AF37] font-bold text-sm">${e.minPrice}</span>
              </div>
              <div className="flex items-center justify-between mt-3">
                <span className="text-xs px-2 py-1 bg-[#9B59B6]/20 text-[#9B59B6] rounded-full capitalize">
                  {e.category}
                </span>
                <div className="flex gap-2">
                  <button onClick={() => openEdit(e)}
                    className="w-7 h-7 rounded-lg bg-[#2D5BE3]/10 hover:bg-[#2D5BE3]/20
                      flex items-center justify-center text-[#2D5BE3] transition-colors">
                    <Pencil size={13} />
                  </button>
                  <button onClick={() => remove(e)}
                    className="w-7 h-7 rounded-lg bg-red-500/10 hover:bg-red-500/20
                      flex items-center justify-center text-red-400 transition-colors">
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {modal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-[#111827] border border-[#1E2D45] rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-[#1E2D45]">
              <h3 className="text-white font-bold">{modal === 'add' ? 'Add Event' : 'Edit Event'}</h3>
              <button onClick={close} className="text-[#546E7A] hover:text-white"><X size={20} /></button>
            </div>
            <div className="p-5 space-y-4">
              {/* Image upload */}
              <ImageUpload
                label="Event Image (shown in app)"
                value={form.imageUrl}
                onChange={file => { setImageFile(file); if (!file) f('imageUrl', ''); }}
                progress={uploadProgress}
              />

              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="block text-xs text-[#B0BEC5] mb-1">Title *</label>
                  <input value={form.title} onChange={e => f('title', e.target.value)}
                    className="w-full px-3 py-2 bg-[#1A2235] border border-[#1E2D45] rounded-xl
                      text-white text-sm focus:outline-none focus:border-[#9B59B6]" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs text-[#B0BEC5] mb-1">Description</label>
                  <textarea value={form.description} onChange={e => f('description', e.target.value)} rows={3}
                    className="w-full px-3 py-2 bg-[#1A2235] border border-[#1E2D45] rounded-xl
                      text-white text-sm focus:outline-none focus:border-[#9B59B6] resize-none" />
                </div>
                <div>
                  <label className="block text-xs text-[#B0BEC5] mb-1">Category</label>
                  <select value={form.category} onChange={e => f('category', e.target.value)}
                    className="w-full px-3 py-2 bg-[#1A2235] border border-[#1E2D45] rounded-xl
                      text-white text-sm focus:outline-none focus:border-[#9B59B6]">
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-[#B0BEC5] mb-1">Organizer</label>
                  <input value={form.organizer} onChange={e => f('organizer', e.target.value)}
                    className="w-full px-3 py-2 bg-[#1A2235] border border-[#1E2D45] rounded-xl
                      text-white text-sm focus:outline-none focus:border-[#9B59B6]" />
                </div>
                {[
                  ['Date *', 'date', 'date'], ['Time', 'time', 'time'],
                  ['Venue *', 'venue', 'text'], ['City', 'city', 'text'],
                  ['Country', 'country', 'text'],
                  ['Min Price ($)', 'minPrice', 'number'], ['Max Price ($)', 'maxPrice', 'number'],
                  ['Total Seats', 'totalSeats', 'number'], ['Available Seats', 'availableSeats', 'number'],
                ].map(([label, key, type]) => (
                  <div key={key}>
                    <label className="block text-xs text-[#B0BEC5] mb-1">{label}</label>
                    <input type={type} value={form[key]} onChange={e => f(key, e.target.value)}
                      className="w-full px-3 py-2 bg-[#1A2235] border border-[#1E2D45] rounded-xl
                        text-white text-sm focus:outline-none focus:border-[#9B59B6]" />
                  </div>
                ))}
              </div>

              {/* Toggles */}
              <div className="flex gap-6">
                {[['Active', 'active'], ['Featured', 'featured'], ['Trending', 'trending']].map(([label, key]) => (
                  <div key={key} className="flex items-center gap-2">
                    <button onClick={() => f(key, !form[key])}
                      className={`w-10 h-6 rounded-full transition-colors ${form[key] ? 'bg-[#9B59B6]' : 'bg-[#1E2D45]'}`}>
                      <div className={`w-4 h-4 bg-white rounded-full mx-1 transition-transform
                        ${form[key] ? 'translate-x-4' : 'translate-x-0'}`} />
                    </button>
                    <span className="text-sm text-[#B0BEC5]">{label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex gap-3 p-5 border-t border-[#1E2D45]">
              <button onClick={close} className="flex-1 py-2.5 bg-[#1A2235] border border-[#1E2D45]
                text-[#B0BEC5] rounded-xl text-sm hover:text-white transition-colors">Cancel</button>
              <button onClick={save} disabled={saving}
                className="flex-1 py-2.5 bg-[#9B59B6] hover:bg-[#6C3483] text-white rounded-xl
                  text-sm font-semibold transition-colors disabled:opacity-50">
                {saving ? `Saving... ${uploadProgress > 0 ? `${uploadProgress}%` : ''}` : 'Save Event'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
